import dayjs from 'dayjs'
import { DashboardWidgetType } from 'features/dashboard/types'
import { SocialWebStreamWidget } from 'schemas/SocialWebStreamWidgetSchema'
import WidgetType from 'enums/WidgetType'
import { AudienceStreamWidget } from 'schemas/AudienceStreamWidgetSchema'
import { SocialWebWordCloudWidget } from 'schemas/SocialWebWordCloudWidgetSchema'
import { AudienceWordCloudWidget } from 'schemas/AudienceWordCloudWidgetSchema'
import { SocialWebTimelineWidget } from 'schemas/SocialWebTimelineWidgetSchema'
import { AudienceTimelineWidget } from 'schemas/AudienceTimelineWidgetSchema'
import { YoutubeTimelineWidget } from 'schemas/YoutubeTimelineWidgetSchema'
import { SocialWebTimelineComparisonWidget } from 'schemas/SocialWebTimelineComparisonWidgetSchema'
import { AudienceTimelineComparisonWidget } from 'schemas/AudienceTimelineComparisonWidgetSchema'
import { AudienceUsageAndAttitudeWidget } from 'schemas/AudienceUsageAndAttitudeWidgetSchema'
import { SocialWebDayAndHourWidget } from 'schemas/SocialWebDayAndHourWidgetSchema'
import { AudienceDayAndHourWidget } from 'schemas/AudienceDayAndHourWidgetSchema'
import { AudienceCrosstabWidget } from 'schemas/AudienceCrosstabWidgetSchema'
import { SocialWebCrosstabWidget } from 'schemas/SocialWebCrosstabWidgetSchema'
import { SocialWebSideBySideWidget } from 'schemas/SocialWebSideBySideWidgetSchema'
import { ThemeInfoList } from 'schemas/ThemeInfoListSchema'
import { ThemeInfo } from 'schemas/ThemeInfoSchema'
import { SocialWebSourcesWidget } from 'schemas/SocialWebSourcesWidgetSchema'
import { SocialWebLanguageWidget } from 'schemas/SocialWebLanguageWidgetSchema'
import { AudienceLanguageWidget } from 'schemas/AudienceLanguageWidgetSchema'
import { getTimeZoneId, TimeZoneInfo, TimeZoneStrings } from 'enums/TimeZone'

export const getThemeInfos = (widgetSetting: DashboardWidgetType) =>
  widgetSetting?.themeInfos?.reduce((acc: ThemeInfo[], themeList: ThemeInfoList) => {
    if (themeList?.themeInfos?.length) {
      acc.push(...themeList?.themeInfos)
    }
    return acc
  }, [])

export const isStreamWidget = (
  widget: DashboardWidgetType
): widget is SocialWebStreamWidget | AudienceStreamWidget =>
  widget.type === WidgetType.SOCIAL_WEB_STREAM || widget.type === WidgetType.AUDIENCE_STREAM

export const isWordCloudWidget = (
  widget: DashboardWidgetType
): widget is SocialWebWordCloudWidget | AudienceWordCloudWidget =>
  widget.type === WidgetType.SOCIAL_WEB_WORDCLOUD ||
  widget.type === WidgetType.AUDIENCE_WORDCLOUD ||
  widget.type === WidgetType.SOCIAL_WEB_SENTIMENT_DRIVERS ||
  widget.type === WidgetType.AUDIENCE_SENTIMENT_DRIVERS ||
  widget.type === WidgetType.SOCIAL_WEB_PHOTO_CONCEPTS

export const isSourcesWidget = (widget: DashboardWidgetType): widget is SocialWebSourcesWidget =>
  widget.type === WidgetType.SOCIAL_WEB_SOURCES

export const isLanguageWidget = (
  widget: DashboardWidgetType
): widget is SocialWebLanguageWidget | AudienceLanguageWidget =>
  widget.type === WidgetType.SOCIAL_WEB_LANGUAGE || widget.type === WidgetType.AUDIENCE_LANGUAGE

export const isTimelineWidget = (
  widget: DashboardWidgetType
): widget is
  | SocialWebTimelineWidget
  | AudienceTimelineWidget
  | YoutubeTimelineWidget
  | SocialWebTimelineComparisonWidget
  | AudienceTimelineComparisonWidget =>
  widget.type === WidgetType.SOCIAL_WEB_TIMELINE ||
  widget.type === WidgetType.SOCIAL_WEB_TIMELINE_OLD ||
  widget.type === WidgetType.SOCIAL_WEB_TIMELINE_COMPARISON ||
  widget.type === WidgetType.AUDIENCE_TIMELINE ||
  widget.type === WidgetType.AUDIENCE_TIMELINE_OLD ||
  widget.type === WidgetType.AUDIENCE_TIMELINE_COMPARISON ||
  widget.type === WidgetType.YOUTUBE_TIMELINE

export const isTimelineComparisonWidget = (
  widget: DashboardWidgetType
): widget is SocialWebTimelineComparisonWidget | AudienceTimelineComparisonWidget =>
  widget.type === WidgetType.SOCIAL_WEB_TIMELINE_COMPARISON ||
  widget.type === WidgetType.AUDIENCE_TIMELINE ||
  widget.type === WidgetType.AUDIENCE_TIMELINE_OLD ||
  widget.type === WidgetType.AUDIENCE_TIMELINE_COMPARISON

export const isAudienceUsageAndAttitudeWidget = (
  widget: DashboardWidgetType
): widget is AudienceUsageAndAttitudeWidget =>
  widget.type === WidgetType.AUDIENCE_USAGE_AND_ATTITUDE

export const isDayHourWidget = (
  widget: DashboardWidgetType
): widget is SocialWebDayAndHourWidget | AudienceDayAndHourWidget =>
  widget.type === WidgetType.SOCIAL_WEB_DAY_AND_HOUR ||
  widget.type === WidgetType.AUDIENCE_DAY_AND_HOUR

export const isCrossTabWidgetOrSideBySideWidget = (
  widget: DashboardWidgetType
): widget is SocialWebCrosstabWidget | AudienceCrosstabWidget | SocialWebSideBySideWidget =>
  widget.type === WidgetType.SOCIAL_WEB_CROSSTAB ||
  widget.type === WidgetType.AUDIENCE_CROSSTAB ||
  widget.type === WidgetType.SOCIAL_WEB_SIDE_BY_SIDE

export const isCrossTabWidget = (
  widget: DashboardWidgetType
): widget is SocialWebCrosstabWidget | AudienceCrosstabWidget =>
  widget.type === WidgetType.SOCIAL_WEB_CROSSTAB || widget.type === WidgetType.AUDIENCE_CROSSTAB

export const getDisplayTimeZone = (timeZoneKey?: TimeZoneStrings) => {
  const tzId = timeZoneKey ? getTimeZoneId(timeZoneKey) : getTimeZoneId()

  const cityName = timeZoneKey
    ? TimeZoneInfo[timeZoneKey][1]
    : Object.values(TimeZoneInfo).find((tzInfo) => tzInfo[0] === tzId)?.[1]

  const utcOffset = dayjs().tz(tzId).format('Z')

  return `(GMT${utcOffset}) ${cityName}`
}
